import { Statistic, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { dashboardService } from '@/services'
import { MovementTable } from '@/components/journey/MovementTable'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { PageError, PageLoading } from '@/components/common/PageStates'
import { useRegionStore } from '@/stores/regionStore'
import {
  REGION_LABELS,
  canAccessCustoutValidation,
  type RegionCode,
  type RegionView,
} from '@/config/regions'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ArrowRightLeft,
  Globe2,
  MapPin,
  Package,
  Radio,
  Warehouse,
  type LucideIcon,
} from 'lucide-react'

const { Text } = Typography

const PIE_COLORS = ['#af0000', '#1f2937']

function regionLabel(view: RegionView): string {
  if (view === 'GLOBAL') return 'Global'
  return REGION_LABELS[view]
}

type Kpi = {
  title: string
  value: number
  icon: LucideIcon
  muted?: boolean
}

function KpiCard({ title, value, icon: Icon, muted }: Kpi) {
  return (
    <div className="overview-kpi">
      <div className="overview-kpi__title">
        <Icon
          className={muted ? 'text-muted-foreground' : 'text-primary'}
          size={14}
        />
        <span>{title}</span>
      </div>
      <Statistic
        value={value}
        valueStyle={{
          color: muted ? '#6b7280' : 'var(--foreground)',
          fontSize: 24,
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      />
    </div>
  )
}

function RegionKpiCard({ view }: { view: RegionView }) {
  return (
    <div className="overview-kpi overview-kpi--region" aria-label={`Selected region: ${regionLabel(view)}`}>
      <div className="overview-kpi__title">
        <Globe2 className="text-primary" size={14} />
        <span>Selected region</span>
      </div>
      <div className="overview-kpi__region">{regionLabel(view)}</div>
    </div>
  )
}

export default function DashboardHomePage() {
  const regionView = useRegionStore((s) => s.view)
  const availableRegions = useRegionStore((s) => s.availableRegions)
  const showValidation = canAccessCustoutValidation(regionView, availableRegions)
  const summaryQ = useQuery({
    queryKey: ['dashboard', 'summary', regionView],
    queryFn: () => dashboardService.getSummary(),
  })

  const movementsQ = useQuery({
    queryKey: ['dashboard', 'movements', 'home', regionView],
    queryFn: () =>
      dashboardService.getMovements({ page: 1, limit: 20, tag_kind: 'all' }),
  })

  if (summaryQ.isLoading) {
    return (
      <PageContainer fluid className="overview-page">
        <PageHeader title="Control Tower" subtitle="Loading live visibility…" />
        <PageLoading />
      </PageContainer>
    )
  }

  if (summaryQ.isError) {
    return (
      <PageContainer fluid className="overview-page">
        <PageHeader title="Control Tower" />
        <PageError
          message="Failed to load dashboard summary"
          description={(summaryQ.error as { message?: string })?.message}
        />
      </PageContainer>
    )
  }

  const s = summaryQ.data!
  const locationSplit = [
    { name: 'LEAP warehouse', value: s.tagsAtLeapWarehouse },
    { name: 'At customer', value: s.tagsAtCustomer },
  ].filter((d) => d.value > 0)

  const kpis: Kpi[] = [
    {
      title: 'Active tags',
      value: s.scannableTags ?? s.totalTags,
      icon: Radio,
    },
    { title: 'Tags moved today', value: s.tagsMovedToday ?? 0, icon: Package },
    { title: 'Movements today', value: s.movementsToday, icon: ArrowRightLeft },
    { title: 'At LEAP warehouse', value: s.tagsAtLeapWarehouse, icon: Warehouse },
    {
      title: 'Non-scan line items',
      value: s.nonScanMaterialsExcluded ?? 0,
      icon: MapPin,
      muted: true,
    },
  ]

  const shortcuts = [
    { to: '/tag-journey', label: 'Tag journey' },
    { to: '/current-state', label: 'Current state' },
    { to: '/movements', label: 'All movements' },
    { to: '/reports', label: 'RFID reports' },
    ...(showValidation ? [{ to: '/validation-report', label: 'RFID validation' }] : []),
  ]

  return (
    <PageContainer fluid className="overview-page">
      <PageHeader
        title="Control Tower"
        subtitle="Live overview of tags and materials captured in RFID Operations — scannable RFID tags and non-scan line items."
      />

      <div className="overview-kpi-grid">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.title} {...kpi} />
        ))}
        <RegionKpiCard view={regionView} />
      </div>

      {s.byRegion && Object.keys(s.byRegion).length > 1 ? (
        <div className="overview-charts-grid">
          {(Object.keys(s.byRegion) as RegionCode[]).map((region) => {
            const rs = s.byRegion![region]!
            return (
              <SectionCard
                key={region}
                className="overview-panel"
                title={`${REGION_LABELS[region]} overview`}
                subtitle="Region-scoped KPIs"
              >
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    Active tags:{' '}
                    <strong>{rs.scannableTags ?? rs.totalTags}</strong>
                  </div>
                  <div>
                    Movements today: <strong>{rs.movementsToday}</strong>
                  </div>
                  <div>
                    At LEAP: <strong>{rs.tagsAtLeapWarehouse}</strong>
                  </div>
                  <div>
                    At customer: <strong>{rs.tagsAtCustomer}</strong>
                  </div>
                </div>
              </SectionCard>
            )
          })}
        </div>
      ) : null}

      <div className="overview-shortcut-grid">
        {shortcuts.map((s) => (
          <Link key={s.to} className="overview-shortcut" to={s.to}>
            <span>{s.label}</span>
            <span aria-hidden>→</span>
          </Link>
        ))}
      </div>

      <div className="overview-charts-grid">
        <SectionCard
          className="overview-panel"
          title="Where tags are now"
          subtitle="Current state by location"
        >
          {locationSplit.length === 0 ? (
            <div className="overview-empty">
              <Text type="secondary">No location data yet.</Text>
            </div>
          ) : (
            <>
              <div className="overview-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={locationSplit}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={46}
                      outerRadius={74}
                      paddingAngle={2}
                    >
                      {locationSplit.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="overview-legend">
                {locationSplit.map((d, i) => (
                  <span key={d.name} className="overview-legend__item">
                    <span
                      className="overview-legend__swatch"
                      style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    {d.name}: <strong>{d.value}</strong>
                  </span>
                ))}
              </div>
            </>
          )}
        </SectionCard>

        <SectionCard
          className="overview-panel"
          title="Movements by type"
          subtitle="All transfer types"
        >
          {s.byTransferType.length === 0 ? (
            <div className="overview-empty">
              <Text type="secondary">No movement mix yet.</Text>
            </div>
          ) : (
            <div className="overview-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={s.byTransferType}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="transferType"
                    tick={{ fontSize: 10 }}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={48}
                  />
                  <YAxis allowDecimals={false} width={36} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#af0000" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard
        className="overview-panel"
        title="Recent movements"
        subtitle="Latest tag and line-item hops"
        variant="table"
      >
        <MovementTable rows={movementsQ.data?.data ?? []} loading={movementsQ.isLoading} />
      </SectionCard>
    </PageContainer>
  )
}
